import { memo } from 'react';

import { Box, FormControl, TextField, Typography } from '@material-ui/core';

export type ResourcesValue = {
  namespaceQuota: string;
  cpuRequest: string;
  cpuLimit: string;
  memoryRequest: string;
  memoryLimit: string;
};

const QUOTA_FIELDS: Array<{
  key: keyof ResourcesValue;
  label: string;
  description: string;
}> = [
  {
    key: 'namespaceQuota',
    label: 'Namespace Quota',
    description: 'Number of namespaces allowed to create in this tenant',
  },
  {
    key: 'cpuRequest',
    label: 'CPU Requests (millicpu)',
    description:
      'Total CPU request quota across all namespaces. In millicpu — e.g. 4000 = 4 cores.',
  },
  {
    key: 'cpuLimit',
    label: 'CPU Limits (millicpu)',
    description:
      'Total CPU limit quota across all namespaces. In millicpu — must be ≥ CPU Requests.',
  },
  {
    key: 'memoryRequest',
    label: 'Memory Requests (MiB)',
    description:
      'Total memory request quota across all namespaces. In MiB — e.g. 8192 = 8 GiB.',
  },
  {
    key: 'memoryLimit',
    label: 'Memory Limits (MiB)',
    description:
      'Total memory limit quota across all namespaces. In MiB — must be ≥ Memory Requests.',
  },
];

type ResourcesSectionProps = {
  value: ResourcesValue;
  onChange: (patch: Partial<ResourcesValue>) => void;
};

export const ResourcesSection = memo(
  ({ value, onChange }: ResourcesSectionProps) => (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Typography variant="h6">Resources</Typography>
      {QUOTA_FIELDS.map(field => {
        const current = value[field.key] ?? '';
        const hasError = !String(current).trim();
        return (
          <FormControl key={field.key} fullWidth required error={hasError}>
            <TextField
              id={field.key}
              label={field.label}
              size="small"
              required
              value={current}
              error={hasError}
              onChange={e => onChange({ [field.key]: e.target.value })}
              helperText={
                hasError ? `${field.label} is required` : field.description
              }
            />
          </FormControl>
        );
      })}
    </Box>
  ),
);
